/**
 * System > 장비 관리
 * 장비 및 그룹 편집
 *
 * @since 2021-05-28
 * @author mk.ko
 */

import { SystemApiPrefix as apiPrefix } from '~/utils/restApi'

const initDeviceGroupInfo = () => {
  return {
    tams_addr_ip: '',
    cfg_admin_id: void 0,
    cfg_dt: '',
    device_api_port: 443,
    device_build_number: void 0, // 빌드 번호
    device_client_val: void 0, // Client ID
    device_desc: '',
    device_dir_type: void 0,
    device_fan_cnt: 3, // fan 개수
    device_firm_val: void 0,
    device_group_name: '',
    device_grp_id: void 0,
    device_gui_client_val: void 0,
    device_gui_secret_val: void 0,
    device_host_val: void 0,
    device_id: void 0,
    device_ip: void 0,
    device_log_relay_yn: void 0,
    device_mach_id: void 0,
    device_memo: void 0,
    device_mmbr_name: void 0,
    device_mmbr_uuid: void 0,
    device_mng_type: 1, // 1: config mode, 2: view mode, 3: monitoring node
    device_mng_val: void 0,
    device_model_name: void 0, // 모델명
    device_model_val: void 0, // 모델 이름
    device_model_type: 20, // MF2 : 9 , MFI : 10 , MFD : 11, MF2 AE : 12, Bluemax NGF : 20
    device_mon_event_inhrit_yn: void 0,
    device_mon_if_auto_update_yn: void 0,
    device_mon_yn: void 0,
    device_os_val: void 0,
    device_policy_mng_yn: void 0,
    device_psu_cnt: 2, // psu 개수
    device_real_ver_val: void 0,
    device_rev_val: void 0, // 리비전
    device_secret_val: void 0,
    device_seq: void 0,
    device_ssh_port: void 0,
    device_type: 0,
    device_uname: '',
    device_ver_val: void 0, // 버전
    device_adm_email_list: [], // 메일
    parent_device_id: void 0,

    db_plain_text: '', // btoa() -> r_cert
    slave_tams_auth_key: '',
    device_auto_add_yn: 1,
    admin_plain_text: '' // btoa() -> device_login_pw

    // forceOverwrite 시 생성됨. (명세용 주석)
    // device_force_add_yn: 0,
    // device_force_add_tams_type: 0
  }
}

const nullToVoid = (target) => {
  try {
    const cloneTarget = _.cloneDeep(target) // reference disconnect
    const keys = _.keys(cloneTarget)
    _.each(keys, key => {
      if (cloneTarget[key] === null) {
        cloneTarget[key] = void 0
      }
    })
    return cloneTarget
  } catch (e) {
    return target
  }
}

export const state = () => ({
  isChanged: false,
  deviceGroupInfo: initDeviceGroupInfo(),
  orgDeviceGroupInfo: initDeviceGroupInfo(),
  monitoringInterfaceData: []
})

export const mutations = {
  initStore (state, ccFlag) {
    state.isChanged = false
    state.deviceGroupInfo = initDeviceGroupInfo()
    if (ccFlag === 1) {
      state.deviceGroupInfo.device_model_type = 9
    }
    state.orgDeviceGroupInfo = initDeviceGroupInfo()
    state.monitoringInterfaceData = []
  },
  setDeviceGroupInfo (state, payload) {
    const deviceGroupInfo = nullToVoid(payload)

    state.deviceGroupInfo = deviceGroupInfo
    state.orgDeviceGroupInfo = _.cloneDeep(deviceGroupInfo)
  },
  /**
   *  deviceGroupInfo 속성 변경
  **/
  updateDeviceGroupInfoProps (state, {key, val}) {
    if (!_.has(state.deviceGroupInfo, key)) return
    _.set(state.deviceGroupInfo, key, val)
  },
  setIsChanged (state, payload) {
    state.isChanged = payload
  },
  updateMonitoringInhrit (state) {
    const deviceGroupInfo = _.cloneDeep(state.deviceGroupInfo)

    state.orgDeviceGroupInfo.device_mon_event_inhrit_yn = deviceGroupInfo.device_mon_event_inhrit_yn
  },
  setMonitoringInterfaceData (state, payload) {
    state.monitoringInterfaceData = payload
  },
  setMonitoringInterfaceDetailInfo (state, payload) {
    let interfaceData = _.cloneDeep(state.monitoringInterfaceData)

    interfaceData = _.map(interfaceData, item => {
      if (item.device_mon_if_id === payload.device_mon_if_id) {
        item = payload
      }
      return item
    })
    state.monitoringInterfaceData = interfaceData
  }
}

export const actions = {
  async getDeviceGroupInfo ({ commit }, { deviceId }) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      res = await this.$axios.$get(`${apiPrefix}/devices/${deviceId}`, {
        loading: false,
        successAlert: false
      })
      if (res.code === 'ok') {
        commit('setDeviceGroupInfo', _.get(res, ['result', '0']))
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async getDeviceGroupImage ({ commit }, { deviceId }) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      res = await this.$axios.$get(`${apiPrefix}/devices/img/${deviceId}`, {
        loading: false,
        successAlert: false
      })
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async getMonitoringInterfaceData ({ commit, state }, { deviceId, deviceMonIfId = null } = {}) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      res = await this.$axios.$post(`${apiPrefix}/devices-mon-if/${deviceId}`, {
        device_mon_if_id: deviceMonIfId
      }, {
        loading: false,
        successAlert: false
      })
      if (res.code === 'ok') {
        commit('setMonitoringInterfaceData', res.result)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async addDevice ({ commit, state }, { isAddDevice, isAddSlave, isAddTamsSlave, isAddSubTams, forceOverwrite } = {}) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      let
        api = `${apiPrefix}/devices`,
        params = {}

      // 각 타입별 장비 추가 (통합)
      // 0: 장비 그룹, 1: Stand-Alone 장비, 2: Master 장비, 3: Slave 장비, 4: TAMS Group, 5: TAMS Master, 6: TAMS Slave, 7: TAMS Sub Root, 8 : TAMS Sub Group
      if (isAddDevice && state.deviceGroupInfo.device_mng_type === 3) { // Monitoring Node
        api = `${apiPrefix}/devices/mon-node`
        const pickList = [
          'parent_device_id', 'device_uname', 'device_ip', 'device_mng_type', 'device_model_type',
          'device_fan_cnt', 'device_psu_cnt', 'device_mach_id', 'device_adm_email_list'
        ]
        params = _.assign(_.pick(state.deviceGroupInfo, pickList), {
          device_type: isAddSlave ? 3 : 1, // 3: Slave 장비, 1: Stand-Alone 장비
          device_desc: state.deviceGroupInfo.device_desc || '',
          device_ver_val: state.deviceGroupInfo.device_ver_val || '',
          device_rev_val: state.deviceGroupInfo.device_rev_val || '',
          device_model_name: state.deviceGroupInfo.device_model_name || ''
        })
      } else if (!isAddDevice) { // Device Group register
        const pickList = ['parent_device_id', 'device_uname']
        params = _.assign(_.pick(state.deviceGroupInfo, pickList), {
          device_type: 0, // Device Group
          device_desc: state.deviceGroupInfo.device_desc || ''
        })
      } else if (isAddDevice) { // Device register
        if (isAddSubTams) { // Sub TAMS register
          api = `${apiPrefix}/devices/sub-tams`
          const pickList = ['device_uname', 'device_ip', 'device_api_port', 'device_adm_email_list', 'tams_addr_ip']
          params = _.assign(_.pick(state.deviceGroupInfo, pickList), {
            login_id: state.deviceGroupInfo.device_login_uname,
            password: _.size(state.deviceGroupInfo.admin_plain_text) > 0 ? btoa(state.deviceGroupInfo.admin_plain_text) : ''
          })
        } else if (isAddTamsSlave) { // TAMS Slave register
          const pickList = ['parent_device_id', 'device_uname', 'device_ip', 'device_api_port', 'slave_tams_auth_key', 'device_adm_email_list', 'tams_addr_ip']
          params = _.assign(_.pick(state.deviceGroupInfo, pickList), {
            device_type: 6, // TAMS Slave
            device_desc: state.deviceGroupInfo.device_desc || '',
            r_cert: _.size(state.deviceGroupInfo.db_plain_text) > 0 ? btoa(state.deviceGroupInfo.db_plain_text) : ''
          })
        } else { // Device Stand-Alone / Master register and Device Slave register
          const pickList = [
            'parent_device_id', 'device_uname', 'device_desc', 'device_ip', 'device_api_port', 'device_mng_type',
            'device_auto_add_yn', 'device_login_uname', 'device_adm_email_list', 'tams_addr_ip'
          ]
          params = _.assign(_.pick(state.deviceGroupInfo, pickList), {
            device_type: isAddSlave ? 3 : 1, // 3: Slave 장비, 1: Stand-Alone 장비
            device_login_pw: _.size(state.deviceGroupInfo.admin_plain_text) > 0 ? btoa(state.deviceGroupInfo.admin_plain_text) : ''
          })
        }

        // Overwrite Device
        if (forceOverwrite) {
          params.device_force_add_yn = 1 // 1
          params.device_force_add_tams_type = state.deviceGroupInfo.device_force_add_tams_type
        }
      }

      res = await this.$axios.$post(api, params)
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async moveDevice ({ commit, state }, { deviceId, deviceSeq, parentDeviceId } = {}) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const
        api = `${apiPrefix}/devices/${deviceId}`,
        params = {
          device_seq: deviceSeq,
          parent_device_id: parentDeviceId
        }

      res = await this.$axios.$put(api, params)
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async editDevice ({ commit, state }, { loading = true, successAlert = true } = {}) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const
        api = `${apiPrefix}/devices`,
        params = state.deviceGroupInfo

      res = await this.$axios.$put(api, params, {
        loading,
        successAlert
      })
      if (res.code === 'ok') {
        commit('setIsChanged', false)
        commit('setDeviceGroupInfo', state.deviceGroupInfo)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async updateDevice ({ commit, state }, { updateDeviceData, forceUpdate = false, isSubTamsRootNode = false } = {}) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const deviceId = updateDeviceData.device_id
      let
        api = `${apiPrefix}/devices/update/${deviceId}`,
        params = {}

      if (isSubTamsRootNode) { // 하위 TAMS 장비 정보 갱신
        api = `${apiPrefix}/devices/sub-tams/${deviceId}`
        params = {
          device_ip: updateDeviceData.device_ip,
          device_api_port: updateDeviceData.device_api_port,
          login_id: updateDeviceData.device_login_uname,
          password: updateDeviceData.device_login_pw, // btoa()
          tams_addr_ip: updateDeviceData.tams_addr_ip
        }
      } else { // 관리 장비 정보 갱신
        params = {
          device_ip: updateDeviceData.device_ip,
          device_api_port: updateDeviceData.device_api_port,
          device_login_uname: updateDeviceData.device_login_uname,
          device_login_pw: updateDeviceData.device_login_pw, // btoa()
          device_mng_type: updateDeviceData.device_mng_type,
          tams_addr_ip: updateDeviceData.tams_addr_ip
        }

        if (forceUpdate) {
          params.device_force_add_yn = 1 // 1
          params.device_force_add_tams_type = updateDeviceData.device_force_add_tams_type
        }
      }

      res = await this.$axios.$put(api, params)

      if (res.code === 'ok') {
        commit('setIsChanged', true)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async editDeviceMonitoringNode ({ commit, state }) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const
        api = `${apiPrefix}/devices/mon-node`,
        params = _.pick(state.deviceGroupInfo, [
          'device_fan_cnt',
          'device_id',
          'device_ip',
          'device_mach_id',
          'device_model_name',
          'device_model_type',
          'device_psu_cnt',
          'device_rev_val',
          'device_ver_val'
        ])

      res = await this.$axios.$put(api, params, {
        loading: false
      })
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async editDeviceMonitoringInhrit ({ commit, state }, { deviceId, inhritYn = 1 } = {}) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const api = `${apiPrefix}/devices/mon-inhrit/${deviceId}/${inhritYn}`

      res = await this.$axios.$put(api, void 0, {
        loading: false,
        successAlert: false
      })
      if (res.code === 'ok') {
        commit('setIsChanged', true)
        commit('updateMonitoringInhrit', inhritYn)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async deleteDevice ({ commit, state }, { deviceId }) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const api = `${apiPrefix}/devices/${deviceId}`
      res = await this.$axios.$delete(api)
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async saveDeviceImage ({ commit, state }, { deviceId, fileData }) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const
        api = `${apiPrefix}/devices/img/${deviceId}`
      const fd = new FormData()

      if (fileData && fileData[0]) {
        fd.append('device_img_act', 'N') // N 일 경우 이미지 사용, 빈 값은 이미지 사용 안함
        fd.append('device_img', fileData[0])
      } else {
        fd.append('device_img_act', '')
        fd.append('device_img', '')
      }

      res = await this.$axios.$put(api, fd, {
        headers: {
          'Content-type': 'multipart/form-data'
        }
      })
      if (res.code === 'ok') {
        commit('setIsChanged', true)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async deleteDeviceImage ({ commit, state }, { deviceId }) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const api = `${apiPrefix}/devices/img/${deviceId}`

      res = await this.$axios.$delete(api)
      if (res.code === 'ok') {
        commit('setIsChanged', true)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async editFailRsrc ({ commit, state }, { deviceId, deviceMonIfId, type, useYn = 0, updateAll = false } = {}) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      // type: 인터페이스 컬럼 타입 (fail: 장애 알람 On/Off, rsrc: 리소스 이벤트 On/Off)
      const
        api = `${apiPrefix}/devices/if/${type}/${deviceId}`,
        ifInfo = _.cloneDeep(state.monitoringInterfaceData)
      const list = []

      _.each(ifInfo, item => {
        if (updateAll || item.device_mon_if_id === deviceMonIfId) {
          // REST API params 생성
          list.push([
            item.device_mon_if_id,
            useYn
          ])

          // 모니터링 인터페이스 데이터 업데이트
          item[`device_mon_if_${type}_event_yn`] = useYn
        }
      })

      const params = {}
      params[`${type}_event_yn_list`] = list

      res = await this.$axios.$put(api, params, {
        loading: false,
        successAlert: false
      })

      if (res.code === 'ok') {
        commit('setIsChanged', true)
        commit('setMonitoringInterfaceData', ifInfo)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async addMonitoringInterface ({ commit, state }, { deviceId, detailInfo }) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const api = `${apiPrefix}/devices-mon-if-set/${deviceId}`
      const params = {
        device_mon_if_mode: detailInfo.device_mon_if_mode,
        device_mon_if_bdwth: detailInfo.device_mon_if_bdwth,
        device_mon_if_fail_event_yn: detailInfo.device_mon_if_fail_event_yn,
        device_mon_if_ip: detailInfo.device_mon_if_ip || '',
        device_mon_if_mac_val: detailInfo.device_mon_if_mac_val,
        device_mon_if_mask_val: detailInfo.device_mon_if_mask_val || '',
        device_mon_if_mng_name: detailInfo.device_mon_if_mng_name,
        device_mon_if_rsrc_event_yn: detailInfo.device_mon_if_rsrc_event_yn,
        device_mon_if_uname: detailInfo.device_mon_if_uname,
        device_mon_if_v6_ip: detailInfo.device_mon_if_v6_ip || '',
        device_mon_if_v6_prefix: detailInfo.device_mon_if_v6_prefix
      }

      res = await this.$axios.$post(api, params)
      if (res.code === 'ok') {
        commit('setIsChanged', true)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async editMonitoringInterface ({ commit, state }, { deviceId, detailInfo }) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const api = `${apiPrefix}/devices-mon-if-set/${deviceId}`
      const params = {
        device_mon_if_id: detailInfo.device_mon_if_id,
        device_mon_if_mode: detailInfo.device_mon_if_mode,
        device_mon_if_bdwth: detailInfo.device_mon_if_bdwth,
        device_mon_if_fail_event_yn: detailInfo.device_mon_if_fail_event_yn,
        device_mon_if_ip: detailInfo.device_mon_if_ip || '',
        device_mon_if_mac_val: detailInfo.device_mon_if_mac_val,
        device_mon_if_mask_val: detailInfo.device_mon_if_mask_val || '',
        device_mon_if_mng_name: detailInfo.device_mon_if_mng_name,
        device_mon_if_rsrc_event_yn: detailInfo.device_mon_if_rsrc_event_yn,
        device_mon_if_uname: detailInfo.device_mon_if_uname,
        device_mon_if_v6_ip: detailInfo.device_mon_if_v6_ip || '',
        device_mon_if_v6_prefix: detailInfo.device_mon_if_v6_prefix
      }
      // device_mon_if_mode

      res = await this.$axios.$put(api, params)
      if (res.code === 'ok') {
        commit('setIsChanged', true)
        commit('setMonitoringInterfaceDetailInfo', detailInfo)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async deleteMonitoringInterface ({ commit, state }, { deviceId, targetItems }) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const
        api = `${apiPrefix}/devices-mon-if-del`,
        params = {
          device_mon_if_id_list: _.map(targetItems, 'device_mon_if_id')
        }

      res = await this.$axios.$put(api, params)
      if (res.code === 'ok') {
        commit('setIsChanged', true)
      }
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  },
  async finalCommand ({ commit, state }, type) {
    let res = {
      code: 'ok',
      result: {}
    }
    try {
      const api = `${apiPrefix}/devices/${type}`
      const axiosConfig = {
        loading: false
      }
      if (type === 'apply') {
        axiosConfig.applyMsg = true
      } else if (type === 'cancel') {
        axiosConfig.cancelMsg = true
      }
      res = await this.$axios.$get(api, axiosConfig)
    } catch (e) {
      res.code = 'nok'
      res.result.msg = e.message
    }
    return res
  }
}

export const getters = {}

export const strict = false
